'use client';
import React, { createContext, useContext, useMemo } from 'react';

type FeatureContextType = {
  isEnabled: (key: string) => boolean;
  enableLgtm: () => boolean;
  enableExtraPage: () => boolean;
  enableGA4: () => boolean;
  publicDeepLink: () => string;
  enableCartDiscountChanges: () => boolean;
  enableMyPhotos: () => boolean;
  enableSortSize: () => boolean;
  enableFacetPagination: () => boolean;
  enableLegalModal: () => boolean;
  enableMasterDeliveryChannel: () => boolean;
  enableScheduleCategoryAnnouncement: () => boolean;
  enableDealsPage: () => boolean;
  enabledIndividualPageForCategories: boolean;
  enabledFreeAbove: () => boolean;
  enableStreetAddressChanges: () => boolean;
};

type FeatureProviderProps = {
  projectSettings?: any;
  children: React.ReactNode;
};

const FeatureContext = createContext<FeatureContextType | null>(null);

export const FeatureProvider = ({ projectSettings = {}, children }: FeatureProviderProps) => {
  const features = useMemo(() => {
    const normalize = (value: any): boolean => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
      }
      if (typeof value === 'number') return value === 1;
      return false;
    };

    const isEnabled = (key: string): boolean => {
      return normalize(projectSettings?.data?.[key]);
    };

    const isDeepLink = (key: string) => {
      return projectSettings?.data?.[key];
    };
    return {
      isEnabled,
      enableLgtm: () => isEnabled('FEATURE_LGTM_FLAG'),
      enableExtraPage: () => isEnabled('EXTENSION_FEATURE_ENABLE_EXTRA_PAGES'),
      enableGA4: () => isEnabled('FEATURE_GA4_FLAG'),
      publicDeepLink: () => isDeepLink('PUBLIC_DEEP_LINK'),
      enableCartDiscountChanges: () => isEnabled('CART_DISCOUNT_CHANGES'),
      enableMyPhotos: () => isEnabled('FEATURE_MY_PHOTOS_FLAG'),
      enableSortSize: () => isEnabled('FEATURE_FLAG_SORT_SIZE'),
      enableFacetPagination: () => isEnabled('ENABLE_FACET_PAGINATION'),
      enableLegalModal: () => isEnabled('FEATURE_FLAG_LEGAL_MODAL'),
      enableMasterDeliveryChannel: () => isEnabled('ENABLE_MASTER_DELIVERY_CHANNEL'),
      enableScheduleCategoryAnnouncement: () => isEnabled('ENABLE_SCHEDULE_CATEGORY_ANNOUNCEMENT'),
      enableDealsPage: () => isEnabled('FEATURE_FLAG_DEALS_PAGE'),
      enabledIndividualPageForCategories: isEnabled('ENABLE_INDIVIDUAL_PAGE_FOR_CATEGORY'),
      enabledFreeAbove: () => isEnabled('FEATURE_FREE_ABOVE'),
      enableStreetAddressChanges: () => isEnabled('STREET_ADDRESS_CHANGES'),
    };
  }, [projectSettings]);

  return <FeatureContext.Provider value={features}>{children}</FeatureContext.Provider>;
};

export const useFeatures = (): FeatureContextType => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within FeatureProvider');
  }
  return context;
};
